import type { ReactNode } from 'react'
import type { Job } from '@workflow/contracts'
import { Avatar, Button, Dialog, IconButton, Icons, ProgressBar, StatusLabel } from '@workflow/ui'
import { navigate } from '@/app/router'
import { useNotes, useSession, useSetJobStatus } from '@/lib/api/hooks'
import { formatCreated, formatDay } from '@/lib/format'
import { personName } from '@/lib/people'
import { clientLabel } from '@/features/clients/clientStatus'
import { formatHours, hoursFigure, partState, statusMoves } from './jobsStatus'
import { JobRowMenu, jobHours, jobTag } from './JobRow'
import { formatMoment } from './JobSchedule'

/**
 * A job at a glance, in a window — the admin twin of the player's task
 * window: its state and the facts as chips, the description, the hours as
 * one large figure with its bar, every player on it with their own hours
 * and state, the latest notes, and the next move. Editing, assigning and
 * the rest live on the full page, one press away.
 */
export function JobQuickView({ job, onClose }: { job: Job | null; onClose: () => void }) {
  return (
    <Dialog open={job !== null} onClose={onClose} labelledBy="job-quick-view-title" className="sm:max-w-xl">
      {job ? <QuickBody key={job.id} job={job} onClose={onClose} /> : null}
    </Dialog>
  )
}

function QuickBody({ job, onClose }: { job: Job; onClose: () => void }) {
  const isAdmin = useSession().data?.account?.role === 'admin'
  const move = useSetJobStatus()
  const notes = useNotes('job', job.id)
  const tag = jobTag(job)
  const hours = jobHours(job)
  // What the hours are measured against: the job's size, else what the crew holds.
  const planned = job.estimatedHours !== null && job.estimatedHours > 0 ? job.estimatedHours : job.assignedHours > 0 ? job.assignedHours : null
  const moves = statusMoves(job, isAdmin)
  // The one move worth a button of its own: finishing, else beginning or resuming.
  const next = moves.find((entry) => entry.target === 'completed')
    ?? moves.find((entry) => entry.target === 'in_progress' || entry.target === 'published')
  const latest = [...(notes.data ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 3)
  const openFull = () => {
    onClose()
    navigate({ name: 'job', id: job.id })
  }

  return (
    <div className="flex max-h-[calc(100dvh-2rem)] flex-col rounded-3xl border bg-surface shadow-modal sm:max-h-[calc(100dvh-4rem)]">
      <div className="flex items-center justify-between gap-3 px-6 pt-5 pb-4 sm:px-8">
        <StatusLabel tone={tag.tone} hue={tag.hue} label={tag.label} />
        <span className="flex items-center gap-1">
          <JobRowMenu job={job} onDeleted={onClose} />
          <IconButton label="Close" size="sm" onClick={onClose} tooltipSide="bottom"><Icons.close /></IconButton>
        </span>
      </div>

      <div className="min-h-0 overflow-y-auto px-6 pb-6 sm:px-8">
        <h2 id="job-quick-view-title" className="wf-heading text-xl text-foreground">{job.title}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <Chip icon={<Icons.user size="sm" />} label="Client">{clientLabel(job)}</Chip>
          <Chip icon={<Icons.tasks size="sm" />} label="Category">{job.typeName}</Chip>
          {job.actualStart ? <Chip icon={<Icons.calendar size="sm" />} label="Started">{`Started ${formatDay(job.actualStart)}`}</Chip> : null}
          {job.scheduledPublishAt ? <Chip icon={<Icons.time size="sm" />} label="Scheduled">{`Publishes ${formatMoment(job.scheduledPublishAt)}`}</Chip> : null}
        </div>

        {job.description ? (
          <p className="mt-5 whitespace-pre-wrap break-words text-ui leading-relaxed text-foreground">{job.description}</p>
        ) : null}

        <section className="mt-6 rounded-3xl bg-tint p-5">
          <p className="text-micro text-muted-foreground uppercase">Hours</p>
          <p className="mt-2 flex items-baseline gap-1.5">
            <span className="font-mono text-4xl leading-none tabular-nums text-foreground">{hoursFigure(job.loggedHours)}</span>
            <span className="text-caption text-muted-foreground">
              {planned !== null ? `Of ${hoursFigure(planned)} Hours Logged` : 'Hours Logged'}
            </span>
          </p>
          {planned !== null ? <ProgressBar value={Math.min(1, hours.value)} label={`${hours.text} on ${job.title}`} className="mt-4 h-1.5" /> : null}
          {/* Every hour logged counts here, finished part or not — the whole
              the crew holds, and what is left of the job's size. */}
          <p className="mt-3 text-caption text-muted-foreground">{hoursLine(job, planned)}</p>
        </section>

        <section className="mt-6">
          <p className="text-micro text-muted-foreground uppercase">Crew</p>
          {job.crew.length === 0 ? (
            <p className="mt-2 text-ui text-muted-foreground">No Crew Yet</p>
          ) : (
            <ul className="mt-2 flex flex-col">
              {job.crew.map((face) => {
                const state = partState(face)
                return (
                  <li key={face.assignmentId} className="flex items-center gap-3 border-b border-hairline py-2.5 last:border-0">
                    <Avatar name={personName(face)} email={face.email} src={face.pictureUrl} size="sm" />
                    <span className="min-w-0 flex-1 truncate text-ui text-foreground">{personName(face)}</span>
                    <StatusLabel className="whitespace-nowrap" tone={state.tone} hue={state.hue} label={state.label} />
                    <span className="flex w-20 justify-end">
                      <span className="whitespace-nowrap rounded-full bg-tint px-2 py-0.5 font-mono text-micro tabular-nums text-foreground">
                        {face.estimatedHours !== null ? `${formatHours(face.actualHours ?? 0)} / ${formatHours(face.estimatedHours)}` : formatHours(face.actualHours ?? 0)}
                      </span>
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {latest.length > 0 ? (
          <section className="mt-6">
            <p className="text-micro text-muted-foreground uppercase">Latest Notes</p>
            <ul className="mt-2 flex flex-col gap-2">
              {latest.map((entry) => (
                <li key={entry.id} className="rounded-2xl bg-tint px-4 py-3">
                  <p className="text-caption text-muted-foreground">{entry.authorName ?? 'The studio'} · {formatCreated(entry.createdAt)}</p>
                  <p className="mt-1 line-clamp-3 whitespace-pre-wrap break-words text-ui text-foreground">{entry.body}</p>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </div>

      {/* The decisions in their own row, each a thumb wide on a phone. */}
      <div className="flex items-center gap-2 border-t border-hairline px-6 py-4 sm:justify-end sm:px-8">
        {next ? (
          <Button
            variant="secondary"
            busy={move.isPending}
            onClick={() => move.mutate({ id: job.id, status: next.target })}
            className="flex-1 sm:flex-none"
          >
            {next.target === 'completed' ? 'Mark As Complete' : next.label}
          </Button>
        ) : null}
        <Button variant="primary" onClick={openFull} className="flex-1 sm:flex-none">Open Full Job</Button>
      </div>
    </div>
  )
}

/** "6h Assigned · 3h Remaining" — the crew's share and what is left, never a count of only the finished parts. */
export function hoursLine(job: Job, planned: number | null): string {
  const assigned = `${formatHours(job.assignedHours)} Assigned`
  if (planned === null) return job.assignedHours > 0 ? assigned : 'No Hours Planned Yet'
  const left = Math.max(0, planned - job.loggedHours)
  // Every part is in but under its hours: what is missing is a shortfall, not work still to come.
  return `${assigned} · ${left === 0 ? 'All Hours Logged' : `${formatHours(left)} ${job.handedInShort ? 'Short' : 'Remaining'}`}`
}

function Chip({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-tint px-3 py-1.5 text-caption text-foreground">
      <span className="text-muted-foreground" aria-hidden>{icon}</span>
      <span className="sr-only">{label}: </span>
      <span className="truncate">{children}</span>
    </span>
  )
}
